import {
  Box,
  Typography,
  Button,
  TextField,
  Stack,
  Link,
  Divider,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAuth } from '@/hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { versionConfig } from '@/config/versions';

type LoginFormData = {
  email: string;
  password: string;
};

const NewLoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login, loginWithGoogle } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  // Check if Google OAuth is configured
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  const isGoogleOAuthEnabled =
    googleClientId && googleClientId !== 'your-google-client-id-here';

  const schema = yup.object({
    email: yup
      .string()
      .email(t('app.auth.please_enter_valid_email'))
      .required(t('app.auth.email_required')),
    password: yup
      .string()
      .min(6, t('app.auth.password_min_6'))
      .required(t('app.auth.password_required')),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      setLoading(true);
      setError(null);
      await login({
        email: data.email.trim(),
        password: data.password,
        rememberMe: false,
      });

      // Trigger profile refetch after login
      // This is needed because useAuth creates local state per component
      const { triggerProfileRefetch } = await import('@/contexts/UserProfileContext');
      await triggerProfileRefetch();

      navigate(versionConfig.getDefaultRoute());
    } catch (err: unknown) {
      let errorMessage: string;

      // Verificar se é um erro 401 (credenciais inválidas)
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { status?: number } };
        if (axiosError.response?.status === 401) {
          errorMessage = t('app.auth.errors.invalid_credentials');
        } else {
          errorMessage = err instanceof Error ? err.message : t('app.auth.errors.login_error');
        }
      } else {
        errorMessage = err instanceof Error ? err.message : t('app.auth.errors.login_error');
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async (credentialResponse: CredentialResponse) => {
    try {
      setLoading(true);
      setError(null);

      if (!credentialResponse.credential) {
        setError(t('app.auth.errors.google_auth_error'));
        return;
      }

      await loginWithGoogle(credentialResponse.credential);

      // Trigger profile refetch after login
      const { triggerProfileRefetch } = await import('@/contexts/UserProfileContext');
      await triggerProfileRefetch();

      navigate(versionConfig.getDefaultRoute());
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : t('app.auth.errors.google_auth_error');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError(t('app.auth.errors.google_auth_error'));
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        backgroundColor: '#f8f9fa',
      }}
    >
      {/* Left Side - Brand Section */}
      <Box
        sx={{
          flex: 3,
          background: 'linear-gradient(135deg, #3b4d66 0%, #2c3e50 100%)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-start',
          padding: { xs: 4, md: 8 },
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background decoration */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
            background: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          style={{ position: 'relative', zIndex: 1, maxWidth: '500px' }}
        >
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 6 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                backgroundColor: '#4A90E2',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
                boxShadow: '0 4px 12px rgba(74, 144, 226, 0.3)',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '1.2rem',
                }}
              >
                AH
              </Typography>
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: '1.1rem',
                color: 'rgba(255, 255, 255, 0.9)',
              }}
            >
              Alley Hooper
            </Typography>
          </Box>

          {/* Main heading */}
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              lineHeight: 1.2,
              mb: 3,
              background: 'linear-gradient(45deg, #ffffff 30%, #e3f2fd 90%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {t('app.auth.brand_title')}
            <br />
            {t('app.auth.brand_title_highlight')}
          </Typography>

          {/* Description */}
          <Typography
            variant="body1"
            sx={{
              fontSize: '1.1rem',
              lineHeight: 1.6,
              color: 'rgba(255, 255, 255, 0.8)',
              maxWidth: '400px',
            }}
          >
            {t('app.auth.brand_description')}
          </Typography>
        </motion.div>
      </Box>

      {/* Right Side - Form Section */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: { xs: 4, md: 8 },
          backgroundColor: 'white',
        }}
      >
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          style={{ width: '100%', maxWidth: '400px' }}
        >
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: '#1a1a1a',
                mb: 2,
                fontSize: { xs: '1.8rem', md: '2rem' },
              }}
            >
              {t('app.auth.sign_in')}
            </Typography>
            {/* <Typography
              variant="body2"
              sx={{
                color: '#666',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              {t('app.auth.already_have_account')}{' '}
              <Link
                component={RouterLink}
                to="/register"
                sx={{
                  color: '#4A90E2',
                  textDecoration: 'none',
                  fontWeight: 500,
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                {t('app.auth.sign_up')}
              </Link>
            </Typography> */}
          </Box>

          {/* Google Sign In - Only show if configured */}
          {isGoogleOAuthEnabled && (
            <>
              <Box sx={{ mb: 3 }}>
                <GoogleLogin
                  onSuccess={handleGoogleSignIn}
                  onError={handleGoogleError}
                  theme="outline"
                  size="large"
                  text="signin_with"
                  width="100%"
                />
              </Box>

              <Divider sx={{ my: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
                  {t('app.auth.or_divider')}
                </Typography>
              </Divider>
            </>
          )}

          {/* Error Alert */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  borderRadius: '8px',
                }}
              >
                {error}
              </Alert>
            </motion.div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={3}>
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    color: '#374151',
                    fontWeight: 500,
                  }}
                >
                  {t('app.auth.email_label')}
                </Typography>
                <TextField
                  {...register('email')}
                  type="email"
                  fullWidth
                  placeholder={t('app.auth.enter_email_placeholder')}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      backgroundColor: '#f9fafb',
                      border: '1px solid #d1d5db',
                      '&:hover': {
                        borderColor: '#9ca3af',
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'white',
                        borderColor: '#4A90E2',
                      },
                      '& fieldset': {
                        border: 'none',
                      },
                    },
                    '& .MuiOutlinedInput-input': {
                      color: '#374151',
                      fontSize: '1rem',
                      // Override browser autofill background
                      '&:-webkit-autofill': {
                        WebkitBoxShadow: '0 0 0 100px #f9fafb inset',
                        WebkitTextFillColor: '#374151',
                        borderRadius: '8px',
                      },
                      '&:-webkit-autofill:hover': {
                        WebkitBoxShadow: '0 0 0 100px #f9fafb inset',
                      },
                      '&:-webkit-autofill:focus': {
                        WebkitBoxShadow: '0 0 0 100px white inset',
                      },
                    },
                  }}
                />
              </Box>

              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#374151',
                      fontWeight: 500,
                    }}
                  >
                    {t('app.auth.password_label')}
                  </Typography>
                  <Link
                    component={RouterLink}
                    to="/forgot-password"
                    sx={{
                      color: '#4A90E2',
                      textDecoration: 'none',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    {t('app.auth.forgot_password_link')}
                  </Link>
                </Box>
                <TextField
                  {...register('password')}
                  type={showPassword ? 'text' : 'password'}
                  fullWidth
                  placeholder={t('app.auth.enter_password_placeholder')}
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      backgroundColor: '#f9fafb',
                      border: '1px solid #d1d5db',
                      '&:hover': {
                        borderColor: '#9ca3af',
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'white',
                        borderColor: '#4A90E2',
                      },
                      '& fieldset': {
                        border: 'none',
                      },
                    },
                    '& .MuiOutlinedInput-input': {
                      color: '#374151',
                      fontSize: '1rem',
                      // Override browser autofill background
                      '&:-webkit-autofill': {
                        WebkitBoxShadow: '0 0 0 100px #f9fafb inset',
                        WebkitTextFillColor: '#374151',
                        borderRadius: '8px',
                      },
                      '&:-webkit-autofill:hover': {
                        WebkitBoxShadow: '0 0 0 100px #f9fafb inset',
                      },
                      '&:-webkit-autofill:focus': {
                        WebkitBoxShadow: '0 0 0 100px white inset',
                      },
                    },
                    '& .MuiInputAdornment-root': {
                      backgroundColor: 'transparent',
                      marginLeft: 0,
                    },
                    '& .MuiIconButton-root': {
                      backgroundColor: 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      },
                    },
                  }}
                />
              </Box>

              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={loading}
                sx={{
                  py: 1.5,
                  color: 'white',
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderRadius: '8px',
                  backgroundColor: '#1f2937',
                  textTransform: 'none',
                  boxShadow: '0 4px 12px rgba(31, 41, 55, 0.2)',
                  '&:hover': {
                    backgroundColor: '#111827',
                    boxShadow: '0 6px 16px rgba(31, 41, 55, 0.3)',
                  },
                  '&:disabled': {
                    backgroundColor: '#e5e7eb',
                    color: '#9ca3af',
                    boxShadow: 'none',
                  },
                }}
              >
                {loading ? t('app.auth.signing_in') : t('app.auth.sign_in')}
              </Button>
            </Stack>
          </form>

          {/* Terms and Privacy */}
          <Typography
            variant="caption"
            sx={{
              display: 'none',
              textAlign: 'center',
              color: '#6b7280',
              mt: 4,
              lineHeight: 1.5,
            }}
          >
            {t('app.auth.terms_agreement')}{' '}
            <Link
              component={RouterLink}
              to="/terms-and-conditions"
              target="_blank"
              sx={{
                color: '#4A90E2',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              {t('app.auth.terms_link')}
            </Link>
            ,<br />
            {t('app.auth.terms_including')}{' '}
            <Link
              component={RouterLink}
              to="/privacy-policy"
              target="_blank"
              sx={{
                color: '#4A90E2',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              {t('app.auth.privacy_policy_link')}
            </Link>
          </Typography>
        </motion.div>
      </Box>
    </Box>
  );
};

export default NewLoginPage;
